<template>
  <el-dialog
    :model-value="visible"
    :title="`${customerName || '客户'} — 请款折扣策略`"
    width="960px"
    destroy-on-close
    @update:model-value="$emit('update:visible', $event)"
  >
    <div class="toolbar">
      <el-button type="primary" :disabled="!canEdit" @click="openForm()">
        <el-icon><Plus /></el-icon> 新增策略
      </el-button>
      <span class="hint">按客户 + 品牌 + 产品类型配置成交价比例；100=原价。新策略生效后不影响已有订单快照。</span>
    </div>

    <el-table v-loading="loading" :data="policies" border size="small" style="width: 100%; margin-top: 12px">
      <el-table-column prop="brand_name" label="品牌" width="120">
        <template #default="{ row }">{{ row.brand_name || '全部' }}</template>
      </el-table-column>
      <el-table-column prop="order_type_name" label="产品类型" width="120">
        <template #default="{ row }">{{ row.order_type_name || '全部' }}</template>
      </el-table-column>
      <el-table-column prop="pay_rate_pct" label="成交价比例(%)" width="130" align="right">
        <template #default="{ row }">{{ formatPayRate(row.pay_rate_pct) }}</template>
      </el-table-column>
      <el-table-column label="生效期" min-width="180">
        <template #default="{ row }">
          {{ row.effective_from || '—' }}
          <span v-if="row.effective_to"> ~ {{ row.effective_to }}</span>
          <span v-else> ~ 长期</span>
        </template>
      </el-table-column>
      <el-table-column prop="priority" label="优先级" width="80" align="center" />
      <el-table-column label="状态" width="120">
        <template #default="{ row }">
          <el-tag v-if="row.is_confirmed" type="success" size="small">已确认</el-tag>
          <el-tag v-else type="info" size="small">草稿</el-tag>
          <el-tag v-if="!row.is_active" type="danger" size="small" style="margin-left: 4px">停用</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="notes" label="备注" min-width="120" show-overflow-tooltip />
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button size="small" :disabled="!canEdit" @click="openForm(row)">编辑</el-button>
          <el-button
            v-if="!row.is_confirmed"
            size="small"
            type="success"
            :disabled="!canEdit"
            @click="handleConfirm(row)"
          >
            确认
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog v-model="formVisible" :title="formTitle" width="520px" append-to-body destroy-on-close>
      <el-form ref="formRef" :model="form" :rules="rules" label-width="120px">
        <el-form-item label="品牌">
          <el-select v-model="form.brand_id" clearable placeholder="全部品牌" style="width: 100%">
            <el-option v-for="b in brandOptions" :key="b.brand_id" :label="b.brand_name" :value="b.brand_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="产品类型">
          <el-select v-model="form.order_type_id" clearable placeholder="全部类型" style="width: 100%">
            <el-option
              v-for="t in orderTypeOptions"
              :key="t.order_type_id"
              :label="t.type_name_cn"
              :value="t.order_type_id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="成交价比例(%)" prop="pay_rate_pct">
          <el-input-number v-model="form.pay_rate_pct" :min="0" :max="100" :precision="2" style="width: 100%" />
          <div class="field-hint">100=原价无折，95=九五折</div>
        </el-form-item>
        <el-form-item label="生效日期" prop="effective_from">
          <el-date-picker v-model="form.effective_from" type="date" value-format="YYYY-MM-DD" style="width: 100%" />
        </el-form-item>
        <el-form-item label="失效日期">
          <el-date-picker v-model="form.effective_to" type="date" value-format="YYYY-MM-DD" clearable style="width: 100%" />
        </el-form-item>
        <el-form-item label="优先级">
          <el-input-number v-model="form.priority" :min="1" :max="9999" style="width: 100%" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.is_active" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.notes" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="formVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveForm">保存</el-button>
      </template>
    </el-dialog>
  </el-dialog>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import { getBrands } from '@/api/brands'
import { getOrderTypes } from '@/api/orderTypes'
import {
  listPaymentDiscountPolicies,
  createPaymentDiscountPolicy,
  updatePaymentDiscountPolicy,
  confirmPaymentDiscountPolicy
} from '@/api/paymentDiscountPolicies'
import { formatPayRate } from '@/utils/paymentRequestDiscount'

const props = defineProps({
  visible: { type: Boolean, default: false },
  customerId: { type: Number, default: null },
  customerName: { type: String, default: '' },
  canEdit: { type: Boolean, default: false }
})

defineEmits(['update:visible'])

const loading = ref(false)
const saving = ref(false)
const policies = ref([])
const brandOptions = ref([])
const orderTypeOptions = ref([])
const formVisible = ref(false)
const formRef = ref()
const editingId = ref(null)

const emptyForm = () => ({
  brand_id: null,
  order_type_id: null,
  pay_rate_pct: 100,
  effective_from: new Date().toISOString().slice(0, 10),
  effective_to: null,
  priority: 100,
  is_active: true,
  notes: ''
})

const form = ref(emptyForm())

const rules = {
  pay_rate_pct: [{ required: true, message: '请填写成交价比例', trigger: 'blur' }],
  effective_from: [{ required: true, message: '请选择生效日期', trigger: 'change' }]
}

const formTitle = computed(() => (editingId.value ? '编辑折扣策略' : '新增折扣策略'))

async function loadMeta () {
  const [brandsRes, typesRes] = await Promise.all([
    getBrands({ per_page: 500 }),
    getOrderTypes({ per_page: 200 })
  ])
  brandOptions.value = brandsRes?.data?.items || brandsRes?.data || []
  orderTypeOptions.value = typesRes?.data?.items || typesRes?.data || []
}

async function loadPolicies () {
  if (!props.customerId) return
  loading.value = true
  try {
    const res = await listPaymentDiscountPolicies({ customer_id: props.customerId })
    policies.value = res?.data || []
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '加载折扣策略失败')
  } finally {
    loading.value = false
  }
}

watch(
  () => [props.visible, props.customerId],
  ([vis, cid]) => {
    if (vis && cid) {
      loadMeta()
      loadPolicies()
    }
  }
)

function openForm (row) {
  if (row) {
    editingId.value = row.policy_id
    form.value = {
      brand_id: row.brand_id ?? null,
      order_type_id: row.order_type_id ?? null,
      pay_rate_pct: Number(row.pay_rate_pct ?? 100),
      effective_from: row.effective_from,
      effective_to: row.effective_to || null,
      priority: row.priority ?? 100,
      is_active: row.is_active !== false,
      notes: row.notes || ''
    }
  } else {
    editingId.value = null
    form.value = emptyForm()
  }
  formVisible.value = true
}

async function saveForm () {
  await formRef.value?.validate()
  saving.value = true
  try {
    const payload = {
      customer_id: props.customerId,
      ...form.value
    }
    if (editingId.value) {
      await updatePaymentDiscountPolicy(editingId.value, payload)
      ElMessage.success('已更新')
    } else {
      await createPaymentDiscountPolicy(payload)
      ElMessage.success('已创建')
    }
    formVisible.value = false
    await loadPolicies()
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '保存失败')
  } finally {
    saving.value = false
  }
}

async function handleConfirm (row) {
  try {
    await ElMessageBox.confirm('确认后策略将参与新订单折扣解析，是否继续？', '确认策略', { type: 'warning' })
    await confirmPaymentDiscountPolicy(row.policy_id)
    ElMessage.success('已确认')
    await loadPolicies()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(e.response?.data?.message || '确认失败')
  }
}
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.hint {
  color: var(--el-text-color-secondary);
  font-size: 12px;
}
.field-hint {
  color: var(--el-text-color-secondary);
  font-size: 12px;
  margin-top: 4px;
}
</style>
